import { useId, useMemo, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import EmptyValue from '@features/shared/components/EmptyValue'
import { IconButton } from '@features/shared/components/IconButton'
import { useClipboardCopy } from '@features/shared/hooks/useClipboardCopy'
import { ValueSelector, type ValueSelectorOption } from '@features/shared/components/ValueSelector'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
} from '@features/shared/icons/icons'
import { useAppNavigation } from '../hooks/useAppNavigation'
import { routeLandingLabel, routeSegmentLabel } from '../logic/appRouteCatalog'
import {
  chooseRouteStep,
  isCompleteRoute,
  isParamSegment,
  parseRoutePath,
  routeFields,
  routePathFromSteps,
  routeStepsWithLanding,
  type RouteField,
  type RouteNode,
  type RouteParams,
  type RouteStep,
} from '../logic/routeCatalog'
import { usePublicRouteCatalog, useRouteCatalog } from './RouteCatalogContext'
import { findRouteParamSource, type RouteParamSource } from '../logic/routeParamSources'

const LANDING_ID = '__landing__'
const FALLBACK_ID = '__fallback__'

/** Levels without a parameter have no values to load. */
const NO_PARAM_SOURCE: RouteParamSource = {
  label: 'Page',
  useOptions: () => ({ options: [], isError: false }),
  loadOptions: () => Promise.resolve([]),
  watchOptions: () => () => undefined,
}

function paramSourceFor(node: RouteNode): RouteParamSource {
  return node.param
    ? (findRouteParamSource(node.param.pattern) ?? NO_PARAM_SOURCE)
    : NO_PARAM_SOURCE
}

function stepOptionId(step: RouteStep) {
  return isParamSegment(step.node.segment) ? `param:${step.value}` : `segment:${step.value}`
}

/**
 * Picks a page in the app by its address, the only thing stored: one field per
 * level of the route, with the levels read from the router. Folded, it shows
 * where the address leads; it reports a new address only once the fields make
 * a whole page. As a `field` it fills a box, the chevron leading. As a `row` (a
 * settings card's), its `leading` label and the address, chevron after it, sit
 * on the two columns of the grid around it (subgrid), the fields opening below
 * across the whole row. As `fields`, it's the fields alone, always open, where
 * the picker is the whole of what's around it and that says where the address
 * leads (Home's card).
 */
export function RoutePicker({
  value,
  onChange,
  ariaLabel,
  disabled = false,
  trailing,
  layout = 'field',
  leading,
  rowEnd,
  emptyLabel = 'Not set',
  includePublicPages = false,
}: {
  value: string | null
  onChange: (value: string) => void
  ariaLabel: string
  disabled?: boolean
  /** A control at the end of the folded row (clear, a lock). */
  trailing?: ReactNode
  layout?: 'field' | 'row' | 'fields'
  /** What starts a `row`, such as its label. */
  leading?: ReactNode
  /** A `row`'s cell after the address, in the grid's next column (an admin's switch). */
  rowEnd?: ReactNode
  /** What the folded field says while no address is set. */
  emptyLabel?: string
  /** Offers the signed-out pages too (Sign In), after the areas, for an email's link. */
  includePublicPages?: boolean
}) {
  const signedInRoot = useRouteCatalog()
  const withPublic = usePublicRouteCatalog()
  const root = includePublicPages ? withPublic.root : signedInRoot
  const { modules } = useAppNavigation()
  // The areas the user can open, in the rail's order; others aren't offered.
  // Signed-out pages, when offered, come after them.
  const topLevelRank = useMemo(() => {
    const ranks = new Map(modules.map((module, index) => [module.pathPrefix, index]))
    const publicSegments = includePublicPages ? withPublic.publicSegments : []
    return (segment: string) =>
      ranks.get(`/${segment}`) ??
      (publicSegments.includes(segment)
        ? modules.length + publicSegments.indexOf(segment)
        : undefined)
  }, [includePublicPages, modules, withPublic.publicSegments])

  const [open, setOpen] = useState(false)
  const savedSteps = useMemo(
    () => (value === null ? null : parseRoutePath(root, value).steps),
    [root, value]
  )
  // The fields as picked; null until something is (an unset value).
  const [draft, setDraft] = useState<RouteStep[] | null>(savedSteps)
  const [syncedValue, setSyncedValue] = useState(value)
  if (value !== syncedValue) {
    setSyncedValue(value)
    setDraft(savedSteps)
  }

  // A table's own address shows as its Views › Default: the level it stands
  // in is shown picked, and the level below it shows the stand-in.
  const steps = routeStepsWithLanding(root, draft ?? [])
  const standInDepth = draft !== null && steps.length > draft.length ? steps.length : null
  const fields = routeFields(root, steps)
  const draftComplete = draft !== null && isCompleteRoute(root, draft)
  const unfinishedField = draftComplete ? undefined : fields.find((field) => field.step === null)

  const commit = (next: RouteStep[]) => {
    setDraft(next)
    if (isCompleteRoute(root, next)) {
      const path = routePathFromSteps(next)
      if (path !== value) onChange(path)
    }
  }
  const pick = (depth: number, choice: { node: RouteNode; value: string } | null) =>
    commit(chooseRouteStep(root, steps, depth, choice))
  // A folded field's item: the collection's segment, then the item, in one pick.
  const pickItem = (depth: number, collection: RouteNode, itemValue: string) => {
    if (!collection.param) return
    const intoCollection = chooseRouteStep(root, steps, depth, {
      node: collection,
      value: collection.segment,
    })
    commit(
      chooseRouteStep(root, intoCollection, depth + 1, { node: collection.param, value: itemValue })
    )
  }

  const toggle = () => {
    // Folding with the fields unfinished goes back to the saved address.
    if (open && !draftComplete) setDraft(savedSteps)
    setOpen(!open)
  }

  const trailId = useId()
  const panelId = useId()

  const isRow = layout === 'row'
  // The fields alone never fold: they're the whole show.
  const isFieldsOnly = layout === 'fields'
  const chevron = (
    <ChevronRightIcon
      size={12}
      aria-hidden="true"
      className={clsx('shrink-0 text-muted transition-transform', open ? 'rotate-90' : '')}
    />
  )

  const button = (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      aria-expanded={open}
      aria-controls={panelId}
      aria-label={ariaLabel}
      aria-describedby={trailId}
      className={clsx(
        'flex min-w-0 flex-1 items-center gap-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:text-muted',
        isRow
          ? clsx('h-11 justify-end text-right text-subtle', !disabled && 'hover:text-default')
          : 'h-full text-left text-subtle'
      )}
    >
      {isRow ? null : chevron}
      <span id={trailId} className={clsx('min-w-0 truncate', !isRow && 'flex-1')}>
        {savedSteps === null ? (
          <span className="font-normal text-muted">{emptyLabel}</span>
        ) : (
          <RouteTrail root={root} steps={savedSteps} />
        )}
      </span>
      {isRow ? (
        // The compound selector's chevron, so it lines up with the fields' below.
        <span className="flex h-full w-7 shrink-0 items-center justify-center text-faint">
          <ChevronDownIcon
            size={9}
            aria-hidden="true"
            className={clsx('transition-transform', open && 'rotate-180')}
          />
        </span>
      ) : null}
    </button>
  )

  return (
    // A row lays its label and address on the two columns of the settings grid around it.
    <div className={isRow ? 'col-span-full grid grid-cols-subgrid' : 'min-w-0'}>
      {isFieldsOnly ? null : isRow ? (
        <div className="route-picker-row col-span-full grid min-h-11 grid-cols-subgrid items-center px-3">
          <div className="route-picker-row-label min-w-0">{leading}</div>
          <div className="flex min-w-0 items-center gap-2">
            {button}
            {trailing}
          </div>
          {rowEnd ? <div className="flex items-center justify-end">{rowEnd}</div> : null}
        </div>
      ) : (
        <div className="flex h-9 min-w-0 items-center gap-2 px-2">
          {button}
          {trailing}
        </div>
      )}

      {open || isFieldsOnly ? (
        <div
          id={panelId}
          className={clsx(
            'space-y-2',
            // A row's fields sit under a faint line inset like its card's dividers.
            isFieldsOnly
              ? ''
              : isRow
                ? "relative col-span-full px-3 pb-2.5 pt-2 before:pointer-events-none before:absolute before:inset-x-3 before:top-0 before:h-px before:bg-[color:var(--border-muted)] before:content-['']"
                : 'border-t border-default px-2 pb-2.5 pt-2'
          )}
        >
          {fields.map((field) => (
            <RoutePickerField
              key={field.node.pattern}
              root={root}
              field={field}
              fallbackDepth={nearestPageDepth(root, steps, field.depth)}
              standingIn={field.depth === standInDepth}
              unset={draft === null}
              disabled={disabled}
              topLevelRank={topLevelRank}
              onPick={pick}
              onPickItem={pickItem}
            />
          ))}
          {unfinishedField ? (
            <p className="pl-[5.5rem] text-xs text-muted">
              Choose a {fieldLabel(unfinishedField).toLowerCase()} to finish.
            </p>
          ) : null}
          <RoutePickerAddress path={draftComplete ? routePathFromSteps(draft) : null} />
        </div>
      ) : null}
    </div>
  )
}

/** How many steps lead to the nearest page above a level, or null if none does. */
function nearestPageDepth(root: RouteNode, steps: readonly RouteStep[], depth: number) {
  for (let candidate = depth; candidate >= 0; candidate -= 1) {
    const node = candidate === 0 ? root : steps[candidate - 1]?.node
    if (node?.isPage) return candidate
  }
  return null
}

function fieldLabel(field: RouteField) {
  if (field.depth === 0) return 'Module'
  if (field.collection) return paramSourceFor(field.collection).label
  return field.node.param ? paramSourceFor(field.node).label : 'Page'
}

function RoutePickerField({
  root,
  field,
  fallbackDepth,
  standingIn,
  unset,
  disabled,
  topLevelRank,
  onPick,
  onPickItem,
}: {
  root: RouteNode
  field: RouteField
  /** The nearest page above this level, for the source's fallback choice. */
  fallbackDepth: number | null
  /** The page above's own address stands in here (a table's Default view): it's picked. */
  standingIn: boolean
  /** No address is set: nothing shows as picked, not even staying on the home page. */
  unset: boolean
  disabled: boolean
  /** An area's place in the rail, or undefined when the user can't open it. */
  topLevelRank: (segment: string) => number | undefined
  onPick: (depth: number, choice: { node: RouteNode; value: string } | null) => void
  onPickItem: (depth: number, collection: RouteNode, itemValue: string) => void
}) {
  const { collection } = field
  // A folded field's values are its collection's items (a grid's views).
  const source = paramSourceFor(collection ?? field.node)
  // The same source for this field's whole life: fields are keyed by node.
  const { options: paramOptions, isError } = source.useOptions(field.params)
  const label = fieldLabel(field)
  const { node, step } = field
  const fallbackLabel = source.fallbackLabel ?? (standingIn ? routeLandingLabel(root, node) : null)

  const options = useMemo(() => {
    const list: ValueSelectorOption[] = []
    // Staying on the page above, or letting it pick, isn't an item of its own:
    // italic, like the app's other stand-in values.
    if (collection) {
      list.push({
        id: LANDING_ID,
        label: source.fallbackLabel ?? routeLandingLabel(root, node),
        italic: true,
      })
    } else if (!field.required) {
      list.push({ id: LANDING_ID, label: routeLandingLabel(root, node), italic: true })
    }
    // Leave the parameter off and let the page above pick (Default).
    if (field.required && fallbackLabel && fallbackDepth !== null) {
      list.push({ id: FALLBACK_ID, label: fallbackLabel, italic: true })
    }
    // A folded field offers the collection's items, not the collection itself.
    const statics = collection
      ? []
      : field.depth === 0
        ? node.statics
            .filter((child) => topLevelRank(child.segment) !== undefined)
            .sort((a, b) => (topLevelRank(a.segment) ?? 0) - (topLevelRank(b.segment) ?? 0))
        : node.statics
    for (const child of statics) {
      list.push({ id: `segment:${child.segment}`, label: routeSegmentLabel(child) })
    }
    for (const option of paramOptions ?? []) {
      list.push({ id: `param:${option.value}`, label: option.label })
    }
    // A stored value whose item is gone still shows, as gone.
    if (step && !list.some((option) => option.id === stepOptionId(step)) && paramOptions) {
      list.push({ id: stepOptionId(step), label: 'Not found', tone: 'danger' })
    }
    return list
  }, [
    collection,
    fallbackDepth,
    fallbackLabel,
    field.depth,
    field.required,
    node,
    paramOptions,
    root,
    source.fallbackLabel,
    step,
    topLevelRank,
  ])

  const selectedId = step
    ? stepOptionId(step)
    : standingIn
      ? FALLBACK_ID
      : field.required || unset
        ? ''
        : LANDING_ID

  return (
    <div className="route-picker-field flex min-w-0 items-center gap-2">
      <span className="w-20 shrink-0 text-xs font-medium text-muted">{label}</span>
      {/* Compact, like the preference values around it. */}
      <div className="min-w-0 flex-1 overflow-hidden rounded-md border border-default">
        <ValueSelector
          variant="compound"
          fullWidth
          showSearch={options.length > 8}
          options={options}
          value={selectedId}
          placeholder={`Choose a ${label.toLowerCase()}`}
          loadingMessage={paramOptions === undefined && !isError ? 'Loading…' : undefined}
          emptyOptionsMessage={isError ? `Couldn't load ${label.toLowerCase()}s` : undefined}
          disabled={disabled}
          ariaLabel={label}
          onChange={(id) => {
            if (id === selectedId) return
            if (id === LANDING_ID) return onPick(field.depth, null)
            if (id === FALLBACK_ID && fallbackDepth !== null) return onPick(fallbackDepth, null)
            if (id.startsWith('segment:')) {
              const segment = id.slice('segment:'.length)
              const child = node.statics.find((candidate) => candidate.segment === segment)
              if (child) onPick(field.depth, { node: child, value: segment })
              return
            }
            if (id.startsWith('param:') && collection) {
              onPickItem(field.depth, collection, id.slice('param:'.length))
              return
            }
            if (id.startsWith('param:') && node.param) {
              onPick(field.depth, { node: node.param, value: id.slice('param:'.length) })
            }
          }}
        />
      </div>
    </div>
  )
}

/** The address, read-only, with a copy button. */
function RoutePickerAddress({ path }: { path: string | null }) {
  const { copied, copyError, isCopying, copy } = useClipboardCopy()

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 pt-0.5">
      <span className="w-20 shrink-0 text-xs font-medium text-muted">URL</span>
      {path ? (
        <>
          {/* eslint-disable-next-line no-restricted-syntax -- a truncated value's full text */}
          <code title={path} className="min-w-0 flex-1 truncate font-mono text-xs text-subtle">
            {path}
          </code>
          <IconButton
            label={copied ? 'Copied' : 'Copy URL'}
            aria-disabled={isCopying || undefined}
            aria-busy={isCopying || undefined}
            className="shrink-0 rounded p-1 text-muted hover:text-default"
            onClick={() => {
              void copy(path)
            }}
          >
            {copied ? (
              <CheckIcon size={13} aria-hidden="true" />
            ) : (
              <CopyIcon size={13} aria-hidden="true" />
            )}
          </IconButton>
        </>
      ) : (
        <span className="min-w-0 flex-1 text-xs">
          <EmptyValue />
        </span>
      )}
      {copyError && (
        <p role="alert" className="basis-full text-xs text-danger">
          {copyError}
        </p>
      )}
    </div>
  )
}

/**
 * Where an address leads, by name: "Tables › Accounts › All Records". A
 * collection's name (Views) gives way to the item picked in it, and an item's
 * first page (a view's Records) goes unsaid.
 */
/** A saved address as the folded picker reads it, such as Automations › Runs. */
export function RoutePathLabel({ value }: { value: string }) {
  const root = useRouteCatalog()
  const steps = useMemo(() => parseRoutePath(root, value).steps, [root, value])
  return <RouteTrail root={root} steps={steps} />
}

function RouteTrail({ root, steps }: { root: RouteNode; steps: readonly RouteStep[] }) {
  const fields = routeFields(root, steps)
  const parts: ReactNode[] = []

  if (steps.length === 0) {
    parts.push(routeLandingLabel(root, root))
  }
  for (const field of fields) {
    const { step } = field
    if (!step) continue
    const { node, parent } = step
    if (isParamSegment(node.segment)) {
      parts.push(
        <RouteParamLabel
          key={node.pattern}
          source={paramSourceFor(parent)}
          params={field.params}
          value={step.value}
        />
      )
      continue
    }
    const isCollection = !node.isPage && node.statics.length === 0 && node.param !== null
    const isItemsFirstPage =
      isParamSegment(parent.segment) && parent.param === null && parent.statics[0] === node
    if (isCollection || isItemsFirstPage) continue
    parts.push(routeSegmentLabel(node))
  }

  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 ? <span className="px-1 text-muted">›</span> : null}
          {part}
        </span>
      ))}
    </>
  )
}

function RouteParamLabel({
  source,
  params,
  value,
}: {
  source: RouteParamSource
  params: RouteParams
  value: string
}) {
  const { options } = source.useOptions(params)
  if (!options) return <span className="text-muted">…</span>
  return <>{options.find((option) => option.value === value)?.label ?? 'Not found'}</>
}
